import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { AutoSizer, CellMeasurer, CellMeasurerCache, List } from 'react-virtualized';
import { debounce } from '../../Utils';
import '../../styles/Common/DropDown.scss'
import { Radio } from '@mui/material';
const DropDown = (props) => {
    const { width = '100%',
        disabled = false,
        label = '',
        id = '',
        option = [],
        Icon = null,
        SearchIcon = null,
        closeIcon = false,
        primaryKey = '',
        labelKey = '',
        preSelectedValues = [],
        onKeyDown = () => { },
        onSelect = () => { },
        multiSelect = false,
        isObject = false,
        headerLabel = label,
        searchPlaceholder = headerLabel,
        showTopLable = true,
    } = props
    const listRef = useRef(null);
    const [displayValue, setDisplayValue] = useState(null)
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredOptions, setFilteredOptions] = useState([]);
    const [selectedValues, setSelectedValues] = useState(preSelectedValues);
    const uniqueOptions = useMemo(() => {
        if (!Array.isArray(option)) {
            console.warn('Option prop is not an array.');
            return [];
        }
        return Array.from(
            new Map(option.map((obj) => [obj[primaryKey], obj])).values()
        );
    }, [option, primaryKey]);

    useEffect(() => {
        setFilteredOptions(uniqueOptions);
    }, [uniqueOptions]);

    useEffect(() => {
        setSelectedValues(preSelectedValues);
        if (multiSelect) {
            if (!isObject && preSelectedValues?.length > 1) {
                setDisplayValue(`All ${label}`)
            } else {
                setDisplayValue(preSelectedValues.map(item => item[labelKey]).join(','));
            }
        } else if (preSelectedValues.length > 0) {
            setDisplayValue(preSelectedValues[0][labelKey]);
        } else {
            setDisplayValue(null);
        }
    }, [preSelectedValues, uniqueOptions, label, primaryKey, labelKey, multiSelect]);

    const updateSearchTerm = useCallback(
        debounce((term) => {
            setFilteredOptions(
                uniqueOptions.filter((obj) =>
                    obj[labelKey]?.toLowerCase().includes(term.toLowerCase())
                )
            );
        }, 100),
        [labelKey, uniqueOptions]
    );

    const handleSearchChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        updateSearchTerm(term);

        cache.current.clearAll();
        if (listRef.current) {
            listRef.current.recomputeRowHeights();
        }
    };
    const openModal = () => {
        if (!disabled) {
            setIsModalOpen(true);
            setFilteredOptions(uniqueOptions);
        }
    };
    const closeModal = () => {
        setIsModalOpen(false)
        setSearchTerm('')
    };


    const handleItemClick = useCallback(
        (item) => {
            let newSelectedValues;
            if (multiSelect) {
                const isSelected = selectedValues.some(selected => selected[primaryKey] === item[primaryKey]);
                newSelectedValues = isSelected
                    ? selectedValues.filter(selected => selected[primaryKey] !== item[primaryKey])
                    : [...selectedValues, item];
                setSelectedValues(newSelectedValues);
                if (!isObject && newSelectedValues?.length > 1) {
                    setDisplayValue(`All ${label}`)
                } else {
                    setDisplayValue(newSelectedValues.map(selected => selected[labelKey]).join(', '));
                }
            } else {
                newSelectedValues = [item];
                setSelectedValues(newSelectedValues);
                setDisplayValue(item[labelKey]);
                closeModal();
            }
            onSelect(newSelectedValues);
            setSearchTerm('')
        },
        [multiSelect, selectedValues, primaryKey, labelKey, onSelect]
    );





    const RowRenderer = useCallback(({ index, key, style, parent }) => {
        const obj = filteredOptions[index];
        const isSelected = selectedValues.some(item => item[primaryKey] === obj[primaryKey]);
        return (
            <CellMeasurer
                key={key}
                cache={cache.current}
                parent={parent}
                columnIndex={0}
                rowIndex={index}
            >
                {({ measure }) => (
                    <li
                        key={key}
                        style={{ ...style, whiteSpace: 'normal' }}
                        onClick={() => handleItemClick(obj)}
                        ref={(el) => {
                            if (el && document.body.contains(el)) {
                                requestAnimationFrame(() => {
                                    if (el.isConnected) measure(); 
                                });
                            }
                        }}
                    >
                        {multiSelect ? (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Radio
                                    checked={isSelected}
                                    name={obj[primaryKey]}
                                    inputProps={{ 'aria-label': obj[labelKey] }}
                                    sx={{ padding: '0px 5px 0px 0px' }}
                                />
                                {obj[labelKey]}
                            </div>
                        ) : (
                            obj[labelKey]
                        )}
                    </li>
                )}
            </CellMeasurer>
        );
    }, [filteredOptions, selectedValues, multiSelect, primaryKey, labelKey]);


    const cache = useRef(
        new CellMeasurerCache({
            fixedWidth: true,
            defaultHeight: 40, 
            minHeight: 30,
        })
    );
    return (
        <div
            className={`dropdown--wrapper  ${disabled ? "disable" : ""}`}
            style={{ width: width }}
        >
            <div className={`input-wrapper ${Icon ? 'with-start-icon' : ''} ${closeIcon ? 'with-end-icon' : ''}`}>
                {Icon && <Icon className='start--icon' sx={{ width: '24px', height: "24px" }} />}
                <input
                    type="text"
                    className={`inp--field  ${(displayValue === null || displayValue === '') ? '' : 'filled'}`}
                    value={displayValue === null || displayValue === '' ? label : displayValue}
                    readOnly
                    id={id}
                    onKeyDown={onKeyDown}
                    onClick={openModal}
                />
                {(closeIcon && (displayValue !== null && displayValue !== '')) &&
                    <CloseIcon className='cross--icon--inp' onClick={() => {
                        setDisplayValue(null)
                        setSelectedValues([]);
                        closeModal()
                        onSelect([])
                    }} />
                }
                <div className={`inp--label ${displayValue !== null && displayValue !== '' && showTopLable ? "filled" : ""}`}>
                    {label}
                </div>
            </div>
            {isModalOpen && (
                <div className="modal--overlay" onClick={closeModal}>
                    <div className="modal--content" onClick={(e) => e.stopPropagation()}>
                        <div className='modal-header'>
                            <ArrowBackIcon onClick={closeModal} style={{ color: '#fff', width: '30px', height: '30px' }} />
                            <span className="header-text">{headerLabel}</span>
                        </div>
                        <div style={{ padding: '12px 16px', background: '#fff' }}>
                            <div className="drop--search">
                                <div className='search--icon'>
                                    {SearchIcon != null && SearchIcon}
                                </div>
                                <input
                                    name={''}
                                    type="text"
                                    className="search"
                                    placeholder={searchPlaceholder}
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    onKeyDown={(e) => { }}
                                    autoFocus
                                    autoComplete={"off"}
                                />
                            </div>
                        </div>
                        <div className='list-header'>
                            <span>Or select from below list</span>
                        </div>
                        <ul className='dropdown--list' >
                            <AutoSizer>
                                {({ height, width }) => (
                                    <List
                                        ref={listRef}
                                        width={width}
                                        height={height}
                                        rowCount={filteredOptions.length}
                                        rowHeight={cache.current.rowHeight} // Use measured row height
                                        rowRenderer={RowRenderer}
                                        overscanRowCount={6}
                                        deferredMeasurementCache={cache.current}
                                        style={{ overflowX: 'hidden' }}
                                    />
                                )}
                            </AutoSizer>
                        </ul>

                    </div>
                </div>
            )
            }
        </div>
    )
}

export default DropDown